import { useCallback, useRef } from 'react';

/**
 * Runs an async action at most once at a time: while one call is still in
 * flight, further calls are ignored and resolve to `undefined`.
 *
 * A React state flag cannot do this on its own (TA-10386 QA finding). Setting
 * `loading` — whether it is Apollo's own mutation state or a local
 * `setSubmitting(true)` — only reaches the button's `disabled` prop on the
 * *next* commit, and React Native hands queued touches to JS in batches. When
 * the JS thread is janked, both taps of a fast double-tap are dispatched before
 * that commit lands, so both reach the handler and both fire the mutation. A
 * ref is written synchronously, so the second call sees it immediately.
 *
 * This is a guard of last resort, not a replacement for disabling the control:
 * the `loading` prop is still what tells the user the action is running.
 *
 * ```ts
 * const runOnce = useSingleFlight();
 * const onConfirm = () => runOnce(async () => { await deleteProject(); });
 * ```
 */
export const useSingleFlight = () => {
  const inFlight = useRef(false);

  return useCallback(async <T>(action: () => Promise<T>) => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    try {
      return await action();
    } finally {
      inFlight.current = false;
    }
  }, []);
};
